// Google Analytics 4 loader — consent-aware (AVG/GDPR compliant).
//
// Werking:
//   1. initAnalytics() wordt bij app-mount aangeroepen
//   2. Als er al consent is opgeslagen (accepted), laadt GA direct
//   3. Anders luistert hij naar 'cookie-consent-changed' event
//      en laadt GA pas zodra user "Accepteer" klikt
//   4. Bij route-change wordt trackPageView() aangeroepen
//   5. Bij Do-Not-Track header (navigator.doNotTrack) wordt GA NIET geladen
//
// Server-side: deze module doet niets tijdens prerender (geen window).

import { getCookieConsent } from './cookie-consent.jsx';

export const GA_MEASUREMENT_ID = 'G-EC3NL6HXST';

let gaLoaded = false;

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function shouldLoadGA() {
  if (!isBrowser()) return false;
  if (!GA_MEASUREMENT_ID) return false;
  // Respecteer Do-Not-Track signal
  if (navigator.doNotTrack === '1' || window.doNotTrack === '1') return false;
  return getCookieConsent() === 'accepted';
}

function loadGAScript() {
  if (gaLoaded) return;
  if (!GA_MEASUREMENT_ID) return;
  gaLoaded = true;

  // Standaard gtag-snippet, opgesplitst zodat we 'em programmatic kunnen laden
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());

  // anonymize_ip = ja (privacy-friendly default)
  // send_page_view = false (we sturen zelf bij route-change, anders dubbel)
  gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  // Eerste pageview meteen sturen
  trackPageView();
}

export function trackPageView(path) {
  if (!isBrowser()) return;
  if (!gaLoaded || !window.gtag) return;
  const p = path || (window.location.pathname + window.location.search);
  window.gtag('event', 'page_view', {
    page_path: p,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(name, params = {}) {
  if (!isBrowser()) return;
  if (!gaLoaded || !window.gtag) return;
  window.gtag('event', name, params);
}

/** Aan te roepen bij app-start. Idempotent. */
export function initAnalytics() {
  if (!isBrowser()) return;

  // Als al consent → meteen laden
  if (shouldLoadGA()) {
    loadGAScript();
    return;
  }

  // Anders luisteren naar consent-event
  window.addEventListener('cookie-consent-changed', () => {
    if (shouldLoadGA()) loadGAScript();
  });
}

/** Voor de cookie-instellingenpagina: user kan consent intrekken. */
export function revokeAnalytics() {
  // GA stoppen met data verzenden door de tracking-cookies te verwijderen
  if (!isBrowser() || !GA_MEASUREMENT_ID) return;
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const name = c.split('=')[0].trim();
    if (name.startsWith('_ga') || name.startsWith('_gid')) {
      // Verwijder cookie voor huidige domein en subdomeinen
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
    }
  }
  // Schakel gtag uit
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
}
